import * as Sentry from '@sentry/react';

import LceCourseExperienceExperiments from 'bundles/epic/clients/__generated__/LceCourseExperience';
import type { ContentSummary } from 'bundles/learner-progress/types/ContentSummary';

type ItemTypeName = ContentSummary['typeName'];

/**
 * A feature flag to guard the injectable activity UI during development and testing.
 * @returns true if https://tools.coursera.org/epic/experiment/Xvjsm0i4EfGL3A6KZpIPHQ is enabled.
 */
export const isActivityInjectionUiEnabled = (): boolean => {
  return LceCourseExperienceExperiments.preview('isActivityInjectionUiEnabled') === true;
};

/**
 * A feature flag to guard the injectable activity API during development and testing.
 * @returns true if https://tools.coursera.org/epic/experiment/97cN0ki3EfGErBK2XQAA3w is enabled.
 */
export const isActivityInjectionApiEnabled = (): boolean => {
  return LceCourseExperienceExperiments.preview('isActivityInjectionApiEnabled') === true;
};

/**
 * An A/B/C test to determine the variant of the activity injection.
 * https://tools.coursera.org/epic/experiment/kXddQUi3EfGErBK2XQAA3w
 * @returns the variant of the activity injection OR undefined if the user is not targeted for the experiment.
 */
export type ActivityInjectionVariant = ReturnType<typeof LceCourseExperienceExperiments.preview<'practiceMoment'>>;
export const previewActivityInjectionVariant = (): ActivityInjectionVariant => {
  return LceCourseExperienceExperiments.preview('practiceMoment');
};
export const impressActivityInjectionVariant = (): ActivityInjectionVariant => {
  return LceCourseExperienceExperiments.get('practiceMoment');
};

const REDESIGNED_COVER_TYPES: ItemTypeName[] = [
  'gradedCoach',
  'coach',
  'ungradedLab',
  'gradedLab',
  'ungradedProgramming',
  'gradedProgramming',
  'ungradedLti',
  'gradedLti',
  'staffGraded',
  'ungradedAssignment',
];

/** Returns true when the item type is currently showing the redesigned ItemCoverPage with its fixed mobile footer. */
export const isRedesignedCoverActive = (typeName: ItemTypeName): boolean => REDESIGNED_COVER_TYPES.includes(typeName);

type ExperimentStartDateConfigKey = 'adaptiveLearningStartDate';

/**
 * Compares the user's enrollment time against an experiment start date stored in an Epic config.
 * The config holds an ISO date string (e.g. "2025-10-05T19:00:00.000Z"); the comparison uses `preview`
 * so it doesn't trigger an impression — callers should chain a `.get()` for the gated variant.
 *
 * @param enrollmentTime - Enrollment time (epoch ms) of the user in the current course.
 * @param configKey - Epic config key holding the experiment start date.
 * @returns true if the user enrolled strictly after the configured start date.
 */
export const isEnrolledAfterExperimentStartDate = (
  enrollmentTime: number | undefined,
  configKey: ExperimentStartDateConfigKey
): boolean => {
  const startDateString = LceCourseExperienceExperiments.preview(configKey);
  if (startDateString && typeof enrollmentTime === 'number') {
    const experimentStartDate = new Date(startDateString);
    if (isNaN(experimentStartDate.getTime())) {
      // invalid date from Epic config
      Sentry.captureException('isEnrolledAfterExperimentStartDate: Invalid date from Epic config', {
        extra: { configKey, startDateString },
      });
      return false;
    }
    const experimentStartTime = experimentStartDate.getTime();
    return enrollmentTime > experimentStartTime;
  }
  return false;
};
